import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { Observable, of, Subscription, Subject } from 'rxjs';
import * as FilterModels from './filter.model';
import { Enums } from '../enums/enums';
import * as FilterPanelTypes from '../sd-filter-panel-module/types/types';
import { SdFilterPanel } from '../sd-filter-panel-module/types/sd-filter-panel';
import { SdFilterPanelInputType, FilterPanelCloseType } from '../sd-filter-panel-module/types/constants';
import { Store } from '@ngrx/store';
import { AppState } from '../../_store/app-state.model';
import { AppSelectors } from '../../_store/selector-types';
import { FilterBarService } from './filter-bar.service';
import { ActivatedRoute } from '@angular/router';
import { tap, switchMap, map, filter, takeUntil } from 'rxjs/operators';
import { AppActions } from '../../_store/action-types';
import { MatChip } from '@angular/material/chips';
import { ValueConverter } from './filter-value-converter';
import { FilterSelectors, FilterActions } from './store';
import { FilterTypes } from './filter.model';
import { LocaleService } from '../locale/locale.service';
import { SharedTranslationService } from '../locale/translation/shared-translation.service';
import { ConfigurationService } from '../services/config/config.service';
import { FilterService } from '../services/services-index';

@Component({
  selector: 'app-filter-bar',
  templateUrl: './filter-bar.component.html',
  styleUrls: ['./filter-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterBarComponent implements OnInit, OnDestroy {
  filters$: Observable<FilterModels.Filter[]>;
  filterTypes$: Observable<FilterModels.FilterType[]>;
  showAddFilter$: Observable<boolean>;
  reportName: string;
  locale: string;
  orgLookupTreeNodes: FilterPanelTypes.IFilterTreeNode[] = [];
  vehicleLookupTreeNodes: FilterPanelTypes.IFilterTreeNode[] = [];
  valueConverter: ValueConverter;
  destroy$ = new Subject<boolean>();

  @ViewChild('addFilterButton') addFilterButton: ElementRef;
  constructor(
    private store$: Store<AppState>,
    private configService: ConfigurationService,
    private route: ActivatedRoute,
    private filterBarService: FilterBarService,
    private filterService: FilterService,
    private filterPanelRef: SdFilterPanel,
    private localeService: LocaleService,
    private translationService: SharedTranslationService
  ) {
    this.valueConverter = new ValueConverter();
  }

  ngOnInit() {
    this.filters$ = this.getCurrentReportFilters().pipe(
      map(filters => filters.filter(f => !(f.visible === false))
      ));
    this.filterTypes$ = this.getCurrentAvailableFilterTypes();
    this.showAddFilter$ = this.filterTypes$.pipe(map(types => types && types.length > 0));

    // todo we could make these more reactive
    this.filterBarService.getOrgLookupFilterNodes().pipe(
      takeUntil(this.destroy$),
      tap(nodes => this.orgLookupTreeNodes = nodes)
    ).subscribe();

    this.store$.select(AppSelectors.selectVehicleLookups).pipe(
      takeUntil(this.destroy$),
      map(vehicleLookups => this.filterBarService.getVehicleLookupFilterNodes(vehicleLookups)),
      tap(nodes => this.vehicleLookupTreeNodes = nodes))
      .subscribe();

    this.localeService.locale$.pipe(tap(locale => this.locale = locale)).subscribe();
  }

  translateLabel = (val) => this.translationService.getLabelTranslation(val, this.locale);

  getCurrentReportFilters() {
    return this.store$.select(AppSelectors.selectCurrentRouteData)
      .pipe(
        switchMap(routeData => {
          this.reportName = routeData.reportName;
          return this.filterBarService.getReportFilters(routeData.reportName);
        })
      );
  }

  getCurrentAvailableFilterTypes() {
    return this.store$.select(AppSelectors.selectCurrentRouteData)
      .pipe(
        switchMap(routeData => this.filterBarService.getAvailableReportFilters(routeData.reportName))
      );
  }

  ngOnDestroy(): void {
  }

  onFilterClicked($event: MouseEvent, chip: ElementRef, filter: FilterModels.Filter) {
    this.show(chip, filter.type, filter.selected);
  }

  onFilterTypeSelected(type: FilterModels.FilterType) {
    this.show(this.addFilterButton, type.name, []);
  }

  show(origin: ElementRef, type: string, selected: FilterModels.FilterValueType[] = []): void {

    const filterType = this.configService.filter.filterConfig.filterTypeLookup[type];

    const filterPanelConfig: FilterPanelTypes.FilterPanelConfig = {
      panelTitle: this.translateLabel(filterType.displayName),
      type: type,
      inputData: null,
    };

    const filterPanelParams: FilterPanelTypes.SdFilterPanelParams = {
      originElement: origin,
      inputDataType: SdFilterPanelInputType.list,
      config: filterPanelConfig
    };

    // cast to simple value and keep handy since it's the most used
    const simpleSelected = <FilterModels.SimpleFilterValue[]>selected;
    const simpleInputPanelData = {
      inputData: simpleSelected.map(s => ({ value: s.value, display: this.translateLabel(s.display) }))
    };

    switch (type) {
      case 'org':
        filterPanelParams.inputDataType = SdFilterPanelInputType.collection;
        filterPanelConfig.singleSelectionMode = true;

        // converted selected values to org tree nodes
        const orgCurrentFilterValues = {
          inputData: (<FilterModels.OrgFilterValue[]>selected).map(s => ({
            display: this.translateLabel(s.display),
            value: `${s.levelTypeId}-${s.value}`,
            orgFilterValue: s
          }))
        };

        filterPanelConfig.inputData = this.filterBarService.getOrgLookupFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = orgCurrentFilterValues;

        break;
      case 'vehicle':
        filterPanelParams.inputDataType = SdFilterPanelInputType.collection;
        filterPanelConfig.singleSelectionMode = true;

        // converted selected values to org tree nodes
        const vehicleCurrentFilterValues = {
          inputData: (<FilterModels.OrgFilterValue[]>selected).map(s => ({
            display: s.display,
            value: s.value,
            orgFilterValue: s
          }))
        };

        filterPanelConfig.inputData = this.filterBarService.getVehicleLookupFilterPanelData();
        filterPanelConfig.currentFilterValues = vehicleCurrentFilterValues;
        break;
      case 'channel':
        const channelFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getChannelFilterPanelData();
        filterPanelConfig.currentFilterValues = channelFilterData;
        break;
      case 'brand':
        const brandFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getBrandFilterPanelData();
        filterPanelConfig.currentFilterValues = brandFilterData;
        break;
      case 'dealer-size':
        const dealerSizeFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        }

        filterPanelConfig.inputData = this.filterBarService.getDealerSizeFilterPanelData();
        filterPanelConfig.currentFilterValues = dealerSizeFilterData;
        break;
       case 'chat-provider':
        const chatProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getChatProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = chatProviderFilterData;
        break;
      case 'trade-in-provider':
        const tradeInProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getTradeInProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = tradeInProviderFilterData;
        break;
      case 'device-type':
        const deviceTypeFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: this.translateLabel(s.display) }))
        };

        filterPanelConfig.inputData = this.filterBarService.getDeviceTypeFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = deviceTypeFilterData;
        break;
      case 'digad-provider':
        const digAdProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getDigAdProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = digAdProviderFilterData;
        break;
      case 'digital-retailing-provider':
        const drProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getDigitalRetailingProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = drProviderFilterData;
        break;
      case 'lead-type':
        filterPanelConfig.inputData = this.filterBarService.getLeadTypeFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = simpleInputPanelData;
        break;
      case 'trait':
        filterPanelConfig.inputData = this.filterBarService.getTraitFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = simpleInputPanelData;
        break;
      case 'profit-center':
        const profitCenterFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };
        filterPanelConfig.singleSelectionMode = true;
        filterPanelConfig.inputData = this.filterBarService.getProfitCenterFilterPanelData();
        filterPanelConfig.currentFilterValues = profitCenterFilterData;
        break;
      case 'digad-program':
        filterPanelConfig.inputData = this.filterBarService.getDigAdProgramFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = simpleInputPanelData;
        break;
      case 'reputation-provider':
        const reputationProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getReputationProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = reputationProviderFilterData;
        break;
      case 'reputation-platform':
        const reputationPlatformFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };
        filterPanelConfig.inputData = this.filterBarService.getReputationPlatformFilterPanelData();
        filterPanelConfig.currentFilterValues = reputationPlatformFilterData;
        break;
      case 'sale-type':
        const saleTypeFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: this.translateLabel(s.display) }))
        };

        filterPanelConfig.inputData = this.filterBarService.getSaleTypeFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = saleTypeFilterData;
        break;
      case 'social-media-platform':
        const socialMediaPlatformFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getSocialMediaPlatformFilterPanelData();
        filterPanelConfig.currentFilterValues = socialMediaPlatformFilterData;
        break;
      case 'social-media-provider':
        const socialProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getSocialMediaProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = socialProviderFilterData;
        break;
      case 'source-type':
        // convert selected values to simple values array
        const sourceTypeFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: this.translateLabel(s.display) }))
        };

        filterPanelConfig.inputData = this.filterBarService.getSourceTypeFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = sourceTypeFilterData;
        break;
      case 'vehicle-make':
        const makeInputFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getVehicleMakeFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = makeInputFilterData;
        break;
      case 'vehicle-class':
        const classInputFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getVehicleClassFilterPanelData();
        filterPanelConfig.currentFilterValues = classInputFilterData;
        break;
      case 'vehicle-model':
        const modelInputFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getVehicleModelFilterPanelData(this.locale);
        filterPanelConfig.currentFilterValues = modelInputFilterData;
        break;
      case 'website-provider':
        const websiteProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getWebsiteProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = websiteProviderFilterData;
        break;
      case 'service-scheduler-provider':
        const serviceSchedulerProviderFilterData = {
          inputData: simpleSelected.map(s => ({ value: s.value, display: s.display }))
        };

        filterPanelConfig.inputData = this.filterBarService.getServiceSchedulerProviderFilterPanelData();
        filterPanelConfig.currentFilterValues = serviceSchedulerProviderFilterData;
        break;
        
    }

    // let currentValuesModel: IFlatFilterModel = {} as IFlatFilterModel;
    // if (currentFilterValues && currentFilterValues.length)
    // 	currentValuesModel.filterData = currentFilterValues;

    const ref = this.filterPanelRef.open(filterPanelParams);

    ref.afterClosed$
      .subscribe((result) => {
        switch (result.dialogData.resultType) {
          case FilterPanelCloseType.applied:
            this.updateFilter(result.dialogData.type, result.dialogData.data);
            break;
          case FilterPanelCloseType.backdropClick:
            break;
          case FilterPanelCloseType.cancel:
            break;
        }
      });
  }

  updateFilter(filterType: string, panelValues: FilterPanelTypes.FilterDataTypeValue[]) {    
    switch (filterType) {
      case 'org':
        // translate for OrgFileValues
        const orgSelected = (<FilterPanelTypes.IFilterTreeNode[]>panelValues).map(this.valueConverter.convertToOrgFilterValue);
        this.filterBarService.updateFilterSelected(this.reportName, filterType, orgSelected);
        break;
      case 'vehicle':
        // translate for OrgFileValues
        const vehicleSelected = (<FilterPanelTypes.IFilterTreeNode[]>panelValues).map(this.valueConverter.convertToOrgFilterValue);
        this.filterBarService.updateFilterSelected(this.reportName, filterType, vehicleSelected);
        break;
      default:
        // translate for SimpleValue (pretty much every thing except org)
        const selected = (<FilterPanelTypes.SimpleValue[]>panelValues).map(this.valueConverter.convertToSimpleFilterValue);
        this.filterBarService.updateFilterSelected(this.reportName, filterType, selected);
    }
  }

  lockFilter(filter: FilterModels.Filter) {
    this.store$.dispatch(FilterActions.toggleFilterLock({ reportName: this.reportName, filter }));
  }

  removeFilter(filterType: string) {
    this.store$.dispatch(FilterActions.removeFilter({ reportName: this.reportName, filterType }));
  }

  resetFilters() {
    this.filterBarService.resetFilters();
  }

  getDisplayValue(filter: FilterModels.Filter, locale: string): string {
    let displayValue = '';

    // determine what the principal display value should be
    if (filter.type === 'org') {
      // if there are no items here, it means we're going national
      if (!filter.selected || filter.selected.length === 0) {
        const topLevel = 1;
        const nationalOrgDisplayName = this.configService.org.getOrgLookupTypeOrDefault(topLevel).name;
        return this.translateLabel(nationalOrgDisplayName);
      }

      const firstItem = <FilterModels.OrgFilterValue>filter.selected[0];
      const translatedFirstItem = this.translateLabel(firstItem.display);
      const displayValueFirstPart = '';

      displayValue = `${displayValueFirstPart}${translatedFirstItem}`;
    } else if (filter.type === 'vehicle') {
      if (!filter.selected || filter.selected.length === 0) {
        return this.translateLabel('All Classes');
      }
      const firstItem = <FilterModels.OrgFilterValue>filter.selected[0];
      const displayValueFirstPart = firstItem.entityType !== 'allclasses'
        ? `${Enums.vehicleEntityTypes[firstItem.entityType].displayName}: `
        : '';

      displayValue = `${displayValueFirstPart}${firstItem.display}`;
    } else {
      const filterType = this.configService.filter.filterConfig.filterTypeLookup[filter.type];
      // if we have none selected, it essentially means all
      if (!filter.selected || filter.selected.length === 0) {
        return `${this.translateLabel(FilterModels.filterTypeLookup[filter.type].displayName)}: ${this.translateLabel('All')}`;
      } else {
        const simpleFirstItem = <FilterModels.SimpleFilterValue>filter.selected[0];
        displayValue = `${this.translateLabel(filterType.displayName)}: ${this.translateLabel(simpleFirstItem.display)}`;
      }
    }

    // if there are more than 1 selected values, show the additional value
    if (filter.selected.length > 1) {
      displayValue += ` +${filter.selected.length - 1} ${this.translateLabel('more')}`;
    }

    return displayValue;
  }

}
